'use client'

import { themes } from '@/app/themes'
import { useDebounce } from '@uidotdev/usehooks'
import { createContext, useEffect, useState } from 'react'
import { defaultTheme } from '../translate/util/constants'
import ThemeContainer from './theme-container'

const pad = (arr: string[]) => [...arr, ...arr, ...arr, ...arr, ...arr]
const langColors = {
    // never change these ids
    dark: pad(['#000000', '#151515', '#292929', '#3e3e3e', '#525252', '#7B7B7B', '#a4a4a4']),
    greys: pad(
        ['#292929', '#343434', '#3e3e3e', '#525252', '#676767', '#7b7b7b', '#909090', '#a4a4a4', '#acacac'].reverse()
    ),
    og: pad(['#f56476', '#e43f6f', '#be3e82', '#D6CA98', '#dfbbb1']),
    purpleToPink: pad(['#f56476', '#ed5273', '#e43f6f', '#d13f79', '#be3e82', '#C253A9', '#C667CF'].reverse()),
    purplish: pad(['#655a7c', '#ab92bf', '#afc1d6', '#cef9f2', '#d6ca98']),
    earthy: pad(['#d4e09b', '#f6f4d2', '#cbdfbd', '#f19c79', '#a44a3f']),
    earthy2: pad(['#394053', '#4e4a59', '#5e575e', '#6e6362', '#797a6b', '#839073', '#809f77', '#7ea779'].reverse()),
    blues: pad(['#a8e0ff', '#8ee3f5', '#70cad1', '#3e517a', '#b08ea2']),
    bw: pad(['#CCCCCC', '#999999', '#666666', '#333333', '#000000']),
    something: pad(['#64113f', '#de4d86', '#f29ca3', '#f7cacd', '#84e6f8']),
    // constrast: pad(['#321325', '#5f0f40', '#7d092f', '#9a031e', '#b33e2c', '#cb793a', '#e4ab44', '#fcdc4d']),
    coffee: pad(['#321325', '#5f0f40', '#7d092f', '#9a031e', '#b33e2c', '#cb793a', '#e4ab44', '#fcdc4d']),
    business: pad(['#2a2b2a', '#5e4955', '#7c596f', '#996888', '#c6ddf0', '#c8bdca', '#c9adb7', '#c99da3'].reverse()),
    jet: pad(['#353535', '#9a9a9a', '#ffffff', '#e9ebef', '#d2d7df', '#c8c9c8', '#bdbbb0', '#a4a296', '#8a897c']),
    spaceCadet: pad([
        '#2d3047',
        '#607483',
        '#93b7be',
        '#bac17d',
        '#e0ca3c',
        '#c4b27a',
        '#a799b7',
        '#56929c',
        '#048a81',
    ]),
    clear: pad(['rgba(255,101,150,.7)', 'rgba(255,101,150,.5)', 'rgba(255,101,150,.3)', 'rgba(255,101,150,.1)']),
    wingIt: pad(['#ff6596', '#ff7e71', '#ff974b', '#ffb026', '#ffc800', '#ff700c', '#ff4412', '#ff2e15']),
    yaleBlue: pad(['#134074', '#13315c', '#0b2545', '#2c4665', '#4c6785', '#8da9c4', '#becfd9', '#eef4ed']),
    timberWolf: pad(['#dad7cd', '#bfc4ac', '#a3b18a', '#7e9971', '#588157', '#496e4c', '#3a5a40', '#344e41']),
    copilot: pad(['#f5f3f7', '#e8e5eb', '#dcd9e0', '#d0cdd6', '#c4c1cc', '#b8b5c2', '#acabb9', '#a09fa9']),
    pinkLavender: pad(['#d1b1c8', '#c1b4cd', '#b1b7d1', '#9b9fb5', '#94899d', '#8c7284', '#7a6174']),
    mindaro: pad(['#ecfeaa', '#e7ee97', '#e2de84', '#c4c27a', '#a6a670', '#86836d', '#626267']),
    // bumble: pad(['#1c110a', '#e4d6a7', '#e6ce91', '#e7ca86', '#e7c57a', '#e8c16f', '#e8bd63', '#e9b958', '#e9b44c']),
    bumblebee: pad(['#1c110a', '#fed801']),
    // emerald: pad(['#231f20', '#2c352e', '#344b3b', '#416c4f', '#56a170', '#66cc8a']),
    emerald: pad(['#304035', '#344b3b', '#3b5c45', '#416c4f', '#56a170', '#5eb77d', '#66cc8a', '#81d59f']),
    halloween: pad(['#ff8f00', '#ef7b21', '#df6741', '#a84e31', '#703421', '#542719']),
    sunset: pad(['#ff0000', '#f73e11', '#f54e15', '#f35d19', '#ef7b21', '#f38f1a', '#f7a213', '#ffc905']),
} as const

export type ColorIds = keyof typeof langColors
export type ThemeId = (typeof themes)[number]

export type ThemeContextProps = {
    colors: string[]
    theme: ThemeId
    setTheme: (theme: ThemeId) => void
    setColorId?: (colorId: ColorIds) => void
    nextColorId: () => void
}

export const ThemeContext = createContext({
    colors: langColors.og,
    theme: defaultTheme,
    setTheme: () => {},
    setColorId: () => {},
    nextColorId: () => {},
} as ThemeContextProps)

function getNextId(currentColorId: ColorIds) {
    const keysToPickFrom = Object.keys(langColors) as ColorIds[]
    const index = keysToPickFrom.indexOf(currentColorId)
    return (keysToPickFrom[index + 1] || keysToPickFrom[0]) as ColorIds
}

function colorIdFromTheme(theme: ThemeId): ColorIds {
    switch (theme) {
        case 'black':
        case 'lofi':
            return 'bw'
        // case 'luxury':
        //     return 'constrast'
        case 'cyberpunk':
            return 'clear'
        // case 'dark':
        //     return 'spaceCadet'
        case 'night':
            return 'blues'
        case 'business':
            return 'business'
        case 'cupcake':
            return 'blues'
        case 'autumn':
        case 'coffee':
            return 'coffee'
        case 'halloween':
            return 'halloween'
        case 'bumblebee':
            return 'bumblebee'
        case 'forest':
        case 'dim':
            return 'earthy2'
        case 'emerald':
            return 'emerald'
        case 'winter':
        case 'aqua':
            return 'yaleBlue'
        case 'nord':
            return 'purplish'
        case 'pastel':
            return 'pinkLavender'
        case 'dracula':
        case 'synthwave':
        case 'valentine':
            return 'purpleToPink'
        case 'sunset':
            return 'sunset'
        default:
            return 'og'
    }
}

type Props = {
    children: React.ReactNode
    currentTheme?: ThemeId
    saveTheme?: (theme: ThemeId) => void
    saveColorId?: (colorId: ColorIds) => void
    currentColorId?: ColorIds
}
const ThemeManager = ({ children, currentTheme: initialTheme, saveColorId, saveTheme }: Props) => {
    const [theme, setTheme] = useState<ThemeId>(initialTheme || defaultTheme)
    const initialColorId: ColorIds = colorIdFromTheme(theme)
    const [colorId, _setColorId] = useState<ColorIds>(initialColorId)
    const colors = langColors[colorId] || langColors.og
    const debouncedColorId = useDebounce(colorId, 50)
    const debouncedTheme = useDebounce(theme, 2000)
    useEffect(() => {
        if (initialTheme !== debouncedTheme) {
            saveTheme?.(debouncedTheme)
        }
    }, [debouncedTheme, initialTheme, saveTheme])
    useEffect(() => {
        if (initialColorId !== debouncedColorId) {
            saveColorId?.(debouncedColorId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedColorId])
    useEffect(() => {
        document.querySelector('html')?.setAttribute('data-theme', theme)
    }, [theme])
    return (
        <ThemeContext
            value={{
                theme,
                setTheme: (theme: ThemeId) => {
                    _setColorId(colorIdFromTheme(theme))
                    setTheme(theme)
                    try {
                        document.cookie = `theme=${theme}; max-age=2147483647`
                    } catch (error) {
                        console.error(error)
                    }
                },
                colors,
                nextColorId: () => {
                    _setColorId(getNextId(colorId))
                },
            }}
        >
            <ThemeContainer theme={theme}>{children}</ThemeContainer>
        </ThemeContext>
    )
}
export default ThemeManager
